class DlCollectionFaqsM extends HTMLElement {
    connectedCallback() {
        if (this._dlCollectionFaqsInit) return;
        this._dlCollectionFaqsInit = true;
        this.initAccordion();
        this.initLearnMoreToggle();
        this.initDescriptionExpand();
    }

    disconnectedCallback() {
        this._dlFaqsResizeAbort?.abort();
        this._dlFaqsResizeAbort = null;
    }

    initDescriptionExpand() {
        const root = this.querySelector('.dl-collection-faqs-header-description');
        const clip = this.querySelector('.dl-collection-faqs-header-description-clip');
        const body = this.querySelector('.dl-collection-faqs-header-description-body');
        const btn = this.querySelector('.description-show-more-show-more-button');
        if (!root || !clip || !body || !btn) return;

        const label = btn.querySelector('.description-show-more-button-label');
        const learnMore = btn.dataset.learnMoreText || 'Learn More';
        const showLess = btn.dataset.showLessText || 'Show Less';
        const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

        const readCollapsedClipPx = () => {
            const prev = clip.style.maxHeight;
            clip.style.removeProperty('max-height');
            const p = parseFloat(window.getComputedStyle(clip).maxHeight);
            clip.style.maxHeight = prev;
            return Number.isFinite(p) && p > 0 ? p : 160;
        };

        const isShort = () => body.scrollHeight <= readCollapsedClipPx() + 2;
        if (isShort()) {
            root.classList.add('is-description-short');
            clip.style.maxHeight = '';
            return;
        }

        let lastCollapsedClipPx = readCollapsedClipPx();

        const refreshCollapsedClipPx = () => {
            if (!root.classList.contains('is-description-expanded')) {
                clip.style.removeProperty('max-height');
                lastCollapsedClipPx = readCollapsedClipPx();
            }
        };

        this._dlFaqsResizeAbort = new AbortController();
        window.addEventListener('resize', refreshCollapsedClipPx, {
            passive: true,
            signal: this._dlFaqsResizeAbort.signal,
        });

        const applyExpanded = (expanded) => {
            root.classList.toggle('is-description-expanded', expanded);
            btn.classList.toggle('is-description-button-expanded', expanded);
            btn.setAttribute('aria-expanded', expanded ? 'true' : 'false');
            if (label) {
                label.textContent = expanded ? showLess : learnMore;
            }

            if (reduceMotion) {
                clip.style.maxHeight = expanded ? 'none' : '';
                if (!expanded) {
                    lastCollapsedClipPx = readCollapsedClipPx();
                }
                return;
            }

            if (expanded) {
                lastCollapsedClipPx = readCollapsedClipPx();
                const target = body.scrollHeight;
                clip.style.maxHeight = `${target}px`;
                clip.addEventListener(
                    'transitionend',
                    function onDescOpenEnd(ev) {
                        if (ev.propertyName !== 'max-height') return;
                        clip.removeEventListener('transitionend', onDescOpenEnd);
                        if (root.classList.contains('is-description-expanded')) {
                            clip.style.maxHeight = 'none';
                        }
                    },
                    { once: true }
                );
            } else {
                const full = clip.scrollHeight;
                clip.style.maxHeight = `${full}px`;
                requestAnimationFrame(() => {
                    requestAnimationFrame(() => {
                        clip.style.maxHeight = `${lastCollapsedClipPx}px`;
                    });
                });
            }
        };

        btn.addEventListener('click', () => {
            applyExpanded(!root.classList.contains('is-description-expanded'));
        });
    }

    initLearnMoreToggle() {
        const list = this.querySelector('.dl-collection-faqs-content-list');
        const btn = this.querySelector('.dl-collection-faqs-content-button');
        const overflow = this.querySelector('.dl-collection-faqs-overflow');
        if (!list || !btn) return;

        const label = btn.querySelector('.dl-collection-faqs-content-button-label');
        const learnMore = btn.dataset.learnMoreText || 'Learn More';
        const showLess = btn.dataset.showLessText || 'Show Less';

        const collapseExtraPanels = () => {
            list.querySelectorAll('.dl-collection-faqs-overflow .dl-collection-faqs-content-list-item').forEach((item) => {
                const header = item.querySelector('.dl-collection-faqs-content-list-item-header');
                const content = item.querySelector('.dl-collection-faqs-content-list-item-content');
                if (!header || !content) return;
                content.classList.remove('is-expanded');
                item.classList.remove('is-open');
                header.setAttribute('aria-expanded', 'false');
            });
        };

        btn.addEventListener('click', () => {
            const willExpand = !list.classList.contains('is-faq-list-expanded');
            list.classList.toggle('is-faq-list-expanded', willExpand);
            btn.classList.toggle('is-list-expanded', willExpand);
            btn.setAttribute('aria-expanded', willExpand ? 'true' : 'false');
            if (overflow) {
                overflow.inert = !willExpand;
            }
            if (label) {
                label.textContent = willExpand ? showLess : learnMore;
            }
            if (!willExpand) {
                collapseExtraPanels();
            }
        });
    }

    initAccordion() {
        const items = Array.from(this.querySelectorAll('.dl-collection-faqs-content-list-item'));
        if (!items.length) return;

        const syncPanelId = (item, index) => {
            const header = item.querySelector('.dl-collection-faqs-content-list-item-header');
            const content = item.querySelector('.dl-collection-faqs-content-list-item-content');
            if (!header || !content) return;
            const id = content.id || `dl-collection-faq-panel-${index + 1}`;
            if (!content.id) content.id = id;
            header.setAttribute('aria-controls', id);
        };

        items.forEach((item, index) => syncPanelId(item, index));

        const setOpen = (targetItem, open) => {
            items.forEach((item) => {
                const header = item.querySelector('.dl-collection-faqs-content-list-item-header');
                const content = item.querySelector('.dl-collection-faqs-content-list-item-content');
                if (!header || !content) return;
                const on = open && item === targetItem;
                content.classList.toggle('is-expanded', on);
                item.classList.toggle('is-open', on);
                header.setAttribute('aria-expanded', on ? 'true' : 'false');
            });
        };

        items.forEach((item) => {
            const header = item.querySelector('.dl-collection-faqs-content-list-item-header');
            const content = item.querySelector('.dl-collection-faqs-content-list-item-content');
            if (!header || !content) return;

            const toggle = () => {
                const isOpen = content.classList.contains('is-expanded');
                if (isOpen) {
                    setOpen(item, false);
                } else {
                    setOpen(item, true);
                }
            };

            header.addEventListener('click', toggle);
            header.addEventListener('keydown', (e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault();
                    toggle();
                }
            });
        });

        const first = items[0];
        const firstContent = first?.querySelector('.dl-collection-faqs-content-list-item-content');
        if (firstContent && !firstContent.classList.contains('is-expanded')) {
            setOpen(first, true);
        }
    }
}

customElements.define('dl-collection-faqs-m', DlCollectionFaqsM);